import { useEffect, useMemo, useState } from "react";
import { Download, FileText, ChevronDown, GraduationCap, Lock } from "lucide-react";
import RightAdRail from "@/components/RightAdRail";
import { api } from "@/lib/api";
import { useMember } from "@/lib/member";

const GRADES = Array.from({ length: 8 }, (_, i) => i + 1);

const SKILLS = [
  { value: "reading", label: "Reading Skills", accent: "bg-[#C7DCF7]", text: "text-[#1F2547]" },
  { value: "grammar", label: "Grammar", accent: "bg-[#D4EDDB]", text: "text-[#1F2547]" },
  { value: "writing", label: "Writing Skills", accent: "bg-[#9F87D8]", text: "text-white" },
  { value: "vocabulary", label: "Vocabulary", accent: "bg-[#1F2547]", text: "text-white" },
];

const CARD_COLOR = {
  purple: "bg-[#9F87D8] text-white",
  blue: "bg-[#C7DCF7] text-[#1F2547]",
  green: "bg-[#D4EDDB] text-[#1F2547]",
  lavender: "bg-[#EFE9F7] text-[#1F2547]",
  navy: "bg-[#1F2547] text-white",
};

function WorksheetCard({ w, member, onSignIn }) {
  const colorClass = CARD_COLOR[w.color] || CARD_COLOR.purple;
  const isDark = colorClass.includes("text-white");

  const handleClick = (e) => {
    if (!member) {
      e.preventDefault();
      onSignIn();
      return;
    }
    if (!w.pdf_url) {
      e.preventDefault();
      return;
    }
    // Log the download (fire-and-forget)
    api.post("/members/downloads", { worksheet_id: w.id }).catch(() => {});
  };

  return (
    <article
      data-testid={`worksheet-card-${w.id}`}
      className={`group relative rounded-2xl brutal-shadow overflow-hidden hover-pop p-5 ${colorClass}`}
    >
      <div className="flex items-center justify-between mb-4">
        <span
          className={`w-11 h-11 rounded-xl flex items-center justify-center ${isDark ? "bg-white/15 backdrop-blur text-white" : "bg-white text-[#9F87D8]"}`}
        >
          <FileText className="w-5 h-5" />
        </span>
        <span
          className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-full ${isDark ? "bg-white/15 backdrop-blur text-white" : "bg-[#1F2547] text-white"}`}
        >
          {w.pages || 0} pgs
        </span>
      </div>

      <h3 className="font-display text-base md:text-lg font-black tracking-tight leading-snug min-h-[3rem]">
        {w.title}
      </h3>

      <a
        href={member && w.pdf_url ? w.pdf_url : "#"}
        onClick={handleClick}
        target={member && w.pdf_url ? "_blank" : undefined}
        rel="noopener noreferrer"
        download={!!(member && w.pdf_url)}
        data-testid={`worksheet-download-${w.id}`}
        className={`mt-5 inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-bold btn-press ${isDark ? "bg-white text-[#1F2547]" : "bg-[#1F2547] text-white"} ${!w.pdf_url && member ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        {member ? (
          <>
            <Download className="w-3.5 h-3.5" />
            {w.pdf_url ? "Download PDF" : "Coming soon"}
          </>
        ) : (
          <>
            <Lock className="w-3.5 h-3.5" />
            Sign in to download — it&apos;s free
          </>
        )}
      </a>
    </article>
  );
}

export default function Worksheets() {
  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grade, setGrade] = useState(1);
  const [skill, setSkill] = useState("reading");
  const { member, signIn } = useMember();

  useEffect(() => {
    api
      .get("/worksheets")
      .then((r) => setWorksheets(r.data))
      .finally(() => setLoading(false));
  }, []);

  // Precompute counts per grade + skill for badges
  const countsByGradeSkill = useMemo(() => {
    const map = {};
    worksheets.forEach((w) => {
      if (!w.grade || !w.skill) return;
      const key = `${w.grade}-${w.skill}`;
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [worksheets]);

  const filtered = worksheets
    .filter((w) => w.grade === Number(grade) && w.skill === skill)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <main data-testid="worksheets-page" className="min-h-[60vh]">
      <RightAdRail />

      {/* HERO */}
      <section className="bg-[#9F87D8]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/80">
            / Worksheets
          </span>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter mt-3 max-w-4xl text-white leading-[1]">
            Practice. Then practice some more.
          </h1>
          <p className="mt-4 text-base md:text-lg text-white/85 font-medium max-w-2xl">
            Printable PDFs organised by grade and skill. Pick a grade, pick a
            skill area — download and learn.
          </p>
        </div>
      </section>

      {/* CONTROLS */}
      <section className="bg-white sticky top-[73px] z-20 border-b border-[#1F2547]/8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex flex-col md:flex-row md:items-center gap-4">
          {/* Grade dropdown */}
          <div className="relative inline-block">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5B6079] block mb-1">
              Grade
            </label>
            <div className="relative">
              <GraduationCap className="w-4 h-4 text-[#9F87D8] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select
                data-testid="worksheets-grade-select"
                value={grade}
                onChange={(e) => setGrade(Number(e.target.value))}
                className="pl-9 pr-9 py-2.5 rounded-full border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-bold text-sm bg-white appearance-none min-w-[160px]"
              >
                {GRADES.map((g) => (
                  <option key={g} value={g}>
                    Grade {g}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-[#5B6079] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          {/* Skill tabs */}
          <div className="flex-1">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5B6079] block mb-1">
              Skill area
            </label>
            <div className="flex flex-wrap gap-2">
              {SKILLS.map((s) => {
                const count = countsByGradeSkill[`${grade}-${s.value}`] || 0;
                const isActive = skill === s.value;
                return (
                  <button
                    key={s.value}
                    data-testid={`worksheets-skill-${s.value}`}
                    onClick={() => setSkill(s.value)}
                    className={`px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-[0.15em] transition-all ${
                      isActive
                        ? `${s.accent} ${s.text} brutal-shadow-sm`
                        : "bg-[#EFE9F7] text-[#1F2547] hover:bg-[#1F2547] hover:text-white"
                    }`}
                  >
                    {s.label}
                    <span className={`ml-2 opacity-70 ${isActive ? "" : ""}`}>
                      ({count})
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        {!member && (
          <div
            data-testid="worksheets-locked-teaser"
            className="mb-8 rounded-2xl bg-[#9F87D8] text-white p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 brutal-shadow-sm"
          >
            <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Lock className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="font-display font-black text-lg">
                Sign in to download — it&apos;s free
              </div>
              <div className="text-sm text-white/85 font-medium">
                Create a free Percept Academy account to grab any worksheet as a PDF.
              </div>
            </div>
            <button
              type="button"
              onClick={signIn}
              data-testid="worksheets-teaser-signin"
              className="px-5 py-2.5 bg-white text-[#1F2547] text-sm font-bold rounded-full btn-press whitespace-nowrap"
            >
              Sign in with Google
            </button>
          </div>
        )}

        <div className="mb-8">
          <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-[#1F2547]">
            Grade {grade} · {SKILLS.find((s) => s.value === skill)?.label}
          </h2>
          <p className="text-sm text-[#5B6079] font-medium mt-1">
            {loading
              ? "Loading…"
              : `${filtered.length} worksheet${filtered.length === 1 ? "" : "s"} available`}
          </p>
        </div>

        {loading ? (
          <div className="text-[#5B6079] font-medium text-center py-16">
            Loading worksheets…
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-3xl bg-white brutal-shadow-sm p-10 text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#EFE9F7] text-[#9F87D8] flex items-center justify-center mx-auto mb-4">
              <FileText className="w-6 h-6" />
            </div>
            <h3 className="font-display font-black text-xl text-[#1F2547]">
              No worksheets yet for this selection
            </h3>
            <p className="text-sm text-[#5B6079] font-medium mt-2 max-w-md mx-auto">
              We&apos;re adding new content weekly. Try another grade or skill,
              or check back soon.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map((w) => (
              <WorksheetCard key={w.id} w={w} member={member} onSignIn={signIn} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
